// A horizontal swipe, shared by every swipe gesture in the app. Once a drag is
// clearly sideways the element follows the finger; on release it either
// leaves on the side it was swiped toward or returns to rest. Movement is
// written to the element directly, so a drag re-renders nothing.
import {
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { motionMs, motionTransition } from "./motion.js";

export type SwipeDirection = "left" | "right";

/** `rest`, `drag`, then `settle` back to rest, or `leave` and `gone`. */
type SwipePhase = "rest" | "drag" | "settle" | "leave" | "gone";

/** Travel before a drag counts as sideways or vertical. */
const SLOP_PX = 8;
/** A release this far along commits: a share of the width, capped. */
const COMMIT_SHARE = 0.4;
const COMMIT_MAX_PX = 120;
/** So does a flick at least this fast that has travelled at least this far. */
const FLING_PX_PER_MS = 0.5;
const FLING_MIN_PX = 24;
/** Speed is measured over the end of the drag. */
const VELOCITY_WINDOW_MS = 100;
/** A release moving back toward rest at least this fast never commits. */
const RETURN_PX_PER_MS = 0.1;
/** How far past its own width a leaving element travels. */
const EXIT_MARGIN_PX = 24;
/** A click this soon after a drag belongs to the drag. */
const CLICK_GUARD_MS = 400;

interface SwipeOptions {
  /** Runs once a swipe commits: after the element has left, or at release
   *  when it returns to rest. */
  onCommit(direction: SwipeDirection): void;
  /** Whether a committed swipe carries the element off its side; it returns
   *  to rest otherwise. True when omitted. */
  exits?: boolean | ((direction: SwipeDirection) => boolean);
  /** The directions the element can be swiped; both when omitted. */
  directions?: readonly SwipeDirection[];
  /** Whether the element fades as it travels; true when omitted. False where
   *  something is revealed behind it. */
  fade?: boolean | ((direction: SwipeDirection) => boolean);
  /** The travel toward the commit point, on every frame: −1 commits to the
   *  left, 1 to the right. */
  onProgress?(progress: number): void;
  disabled?: boolean;
}

interface SwipeHandlers {
  onTouchStart(e: ReactTouchEvent): void;
  onTouchMove(e: ReactTouchEvent): void;
  onTouchEnd(e: ReactTouchEvent): void;
  onTouchCancel(e: ReactTouchEvent): void;
  onClickCapture(e: ReactMouseEvent): void;
}

interface Swipe<T extends HTMLElement> {
  /** The element that moves; nothing moves while it is not attached. */
  ref: RefObject<T>;
  /** For the grab area: the element itself, or a part of it. */
  handlers: SwipeHandlers;
  phase: SwipePhase;
  /** Carry the element off toward `direction`, then commit. */
  leave(direction: SwipeDirection): void;
  /** Bring a gone element back to rest. */
  reset(): void;
}

interface Drag {
  x: number;
  y: number;
  axis: "x" | "y" | null;
  offset: number;
  width: number;
  samples: { t: number; offset: number }[];
}

const DIRECTIONS: readonly SwipeDirection[] = ["left", "right"];
const sign = (d: SwipeDirection) => (d === "left" ? -1 : 1);
const directionOf = (offset: number): SwipeDirection => (offset < 0 ? "left" : "right");

function pick<V>(value: V | ((d: SwipeDirection) => V) | undefined, d: SwipeDirection, or: V): V {
  if (value === undefined) return or;
  return typeof value === "function" ? (value as (d: SwipeDirection) => V)(d) : value;
}

export function commitDistance(width: number): number {
  return width > 0 ? Math.min(width * COMMIT_SHARE, COMMIT_MAX_PX) : COMMIT_MAX_PX;
}

/** Solid until halfway to the commit point, then fading out by the far
 *  edge: what is only being moved stays readable, and what will go on
 *  release shows it. */
function fadeAt(offset: number, width: number): number {
  const from = commitDistance(width) / 2;
  return 1 - Math.min(1, Math.max(0, (Math.abs(offset) - from) / (width - from)));
}

/** Speed over the end of the drag, in px/ms. */
function velocity(samples: Drag["samples"]): number {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last || last.t <= first.t) return 0;
  return (last.offset - first.offset) / (last.t - first.t);
}

/** The side a release at `offset` moving at `speed` commits toward, if any. */
export function releaseDirection(
  offset: number,
  speed: number,
  width: number,
): SwipeDirection | null {
  if (offset === 0) return null;
  const towardRest = Math.sign(speed) === -Math.sign(offset);
  if (towardRest && Math.abs(speed) >= RETURN_PX_PER_MS) return null;
  const far = Math.abs(offset) >= commitDistance(width);
  const flung =
    !towardRest && Math.abs(speed) >= FLING_PX_PER_MS && Math.abs(offset) >= FLING_MIN_PX;
  return far || flung ? directionOf(offset) : null;
}

export function useSwipe<T extends HTMLElement = HTMLDivElement>(options: SwipeOptions): Swipe<T> {
  const ref = useRef<T>(null);
  const [phase, setPhaseState] = useState<SwipePhase>("rest");
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const api = useMemo(() => {
    let current: SwipePhase = "rest";
    let drag: Drag | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let clicksIgnoredUntil = 0;

    const setPhase = (p: SwipePhase) => {
      current = p;
      setPhaseState(p);
    };
    const allowed = (d: SwipeDirection) =>
      (optionsRef.current.directions ?? DIRECTIONS).includes(d);
    const progress = (p: number) => optionsRef.current.onProgress?.(p);

    /** Puts the element `offset` px from rest; an opacity of null is its own. */
    const place = (offset: number, opacity: number | null, animate: boolean) => {
      const el = ref.current;
      if (!el) return;
      el.style.transition = animate ? motionTransition("transform", "opacity") : "none";
      el.style.transform = offset === 0 ? "" : `translate3d(${offset}px, 0, 0)`;
      el.style.opacity = opacity == null ? "" : String(opacity);
    };
    const clear = () => {
      const el = ref.current;
      if (!el) return;
      el.style.transition = "";
      el.style.transform = "";
      el.style.opacity = "";
    };
    /** Runs `then` once an animation started now has finished. */
    const after = (then: () => void) => {
      clearTimeout(timer);
      const ms = motionMs();
      if (ms === 0) then();
      else timer = setTimeout(then, ms);
    };

    const settle = () => {
      setPhase("settle");
      place(0, null, true);
      progress(0);
      after(() => {
        clear();
        setPhase("rest");
      });
    };
    const leave = (direction: SwipeDirection, width: number) => {
      setPhase("leave");
      place(sign(direction) * (width + EXIT_MARGIN_PX), 0, true);
      progress(sign(direction));
      after(() => {
        setPhase("gone");
        optionsRef.current.onCommit(direction);
      });
    };
    /** Follows the finger to `dx` from where the drag started. */
    const follow = (d: Drag, dx: number, t: number) => {
      d.offset = allowed(directionOf(dx)) ? dx : 0;
      d.samples.push({ t, offset: d.offset });
      while (d.samples.length > 1 && t - d.samples[0]!.t > VELOCITY_WINDOW_MS) d.samples.shift();
    };

    const handlers: SwipeHandlers = {
      onTouchStart(e) {
        const t = e.touches[0];
        if (!t || optionsRef.current.disabled) return;
        if (current === "leave" || current === "gone") return;
        if (current === "settle") {
          clearTimeout(timer);
          clear();
          setPhase("rest");
        }
        const measured = ref.current ?? (e.currentTarget as HTMLElement | null);
        drag = {
          x: t.clientX,
          y: t.clientY,
          axis: null,
          offset: 0,
          width: measured?.offsetWidth ?? 0,
          samples: [],
        };
      },
      onTouchMove(e) {
        const d = drag;
        const t = e.touches[0];
        if (!d || !t) return;
        const dx = t.clientX - d.x;
        const dy = t.clientY - d.y;
        if (d.axis === null) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < SLOP_PX) return;
          d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
          if (d.axis === "x") setPhase("drag");
        }
        if (d.axis !== "x") return;
        follow(d, dx, e.timeStamp);
        const fades = d.offset !== 0 && pick(optionsRef.current.fade, directionOf(d.offset), true);
        const opacity = fades && d.width > 0 ? fadeAt(d.offset, d.width) : null;
        place(d.offset, opacity, false);
        progress(d.offset / commitDistance(d.width));
      },
      onTouchEnd(e) {
        const d = drag;
        drag = null;
        if (!d || d.axis !== "x") return;
        clicksIgnoredUntil = Date.now() + CLICK_GUARD_MS;
        const t = e.changedTouches[0];
        follow(d, t ? t.clientX - d.x : d.offset, e.timeStamp);
        const direction = releaseDirection(d.offset, velocity(d.samples), d.width);
        if (!direction) return settle();
        if (pick(optionsRef.current.exits, direction, true)) return leave(direction, d.width);
        optionsRef.current.onCommit(direction);
        settle();
      },
      onTouchCancel() {
        const d = drag;
        drag = null;
        if (d?.axis === "x") settle();
      },
      onClickCapture(e) {
        if (Date.now() >= clicksIgnoredUntil) return;
        e.stopPropagation();
        e.preventDefault();
      },
    };

    return {
      handlers,
      leave(direction: SwipeDirection) {
        if (current === "leave" || current === "gone") return;
        drag = null;
        leave(direction, ref.current?.offsetWidth ?? 0);
      },
      reset() {
        if (current === "gone") settle();
      },
    };
  }, []);

  return { ref, phase, ...api };
}
